/**
 * DemographicsSection — the demographics fields of the "Personal Details"
 * FormView (Flutter `personal_details_form.dart`). The patient photo lives
 * just above this in the tab; the MINOR/ADULT patient-type cards moved to the
 * top of the page (Flutter "I AM ADDING" / "PATIENT IS").
 *
 * Single-column field order — verbatim from Flutter `personal_details_form.dart`:
 *   First name* · Middle name · Last name* · Date of birth* · Gender* (pills) ·
 *   Consent to share data with HIE (pills, default "Yes") · Importance (pills) ·
 *   Marital status (pills) · Preferred language (search) ·
 *   Interpreter required (Yes/No pills) · Religion (search) · Nationality* (search)
 *
 * Pickers that are `FieldType.SingleTagPicker` in Flutter render as pill
 * choosers here (gender, consent, importance, marital, interpreter); the
 * `SearchPicker` fields (language, religion, nationality) stay typeaheads.
 */

import { useState } from 'react';
import { useFormContext, useWatch, Controller } from 'react-hook-form';
import { requiredTone } from './required-tone';
import { TextField, BrandSpinner } from '@scripsteam/scrips-signal-ds';
import { DatePickerField } from '@/shared/ui/components';
import { adultMaxDob, minorMinDob } from './dob-bounds';
import {
  useGenders,
  useReligions,
  useNationalities,
} from '@/shared/services/lookups';
import { MasterTypeahead } from './master-typeahead';
import { usePatientImportance } from '@/patients/services/use-patient-importance';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { PillPicker } from './form-primitives';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import { PatientPreferencesFields } from './patient-preferences-fields';
import type { PersonalDetailsFormValues } from './personal-details-schema';
import { useMissingFields } from './missing-fields-context';

// ── Helpers ────────────────────────────────────────────────────────────────

function birthToDateInput(iso: string | null): string {
  if (!iso) return '';
  // Strip time portion; <input type="date"> expects YYYY-MM-DD.
  return iso.split('T')[0] ?? '';
}

function dateInputToWire(yyyymmdd: string): string {
  // .NET local-DateTime form (no Z) — same as the inline create-patient flow.
  if (!yyyymmdd) return '';
  return `${yyyymmdd}T00:00:00.000`;
}

/** HIE consent options — verbatim from Flutter
 *  `personal_details_form.dart:83-99` (code / display / definition). */
const CONSENT_OPTIONS = [
  { code: '0', display: 'Yes', definition: 'Opt in' },
  { code: '1', display: 'No, opt out this facility only', definition: 'Facility Opt Out' },
  { code: '2', display: 'No, opt out globally', definition: 'Global Opt Out' },
] as const;

// ── DemographicsSection ────────────────────────────────────────────────────

export function DemographicsSection() {
  const { register, control, setValue, formState } =
    useFormContext<PersonalDetailsFormValues>();
  const { errors } = formState;
  const missing = useMissingFields();

  const gender = useWatch({ control, name: 'gender' });
  const firstName = useWatch({ control, name: 'firstName' });
  const lastName = useWatch({ control, name: 'lastName' });
  const birthDate = useWatch({ control, name: 'birthDate' });
  // DOB window depends on the Adult/Minor patient type (Flutter
  // personal_details_form.dart:443-450). Default to adult (Flutter
  // `isAdult ?? true`). Adult ⇒ must be >18 (max = today−18y−1d); Minor ⇒
  // within last 18y (min = today−18y, max = today).
  const isAdult = useWatch({ control, name: 'isAdult' }) ?? true;
  const dobMin = isAdult ? undefined : minorMinDob();
  const dobMax = isAdult ? adultMaxDob() : new Date();
  const importance = useWatch({ control, name: 'importance' });
  const consent = useWatch({ control, name: 'consent' });
  const religion = useWatch({ control, name: 'religion' });
  const nationality = useWatch({ control, name: 'nationality' });

  const gendersQ = useGenders();
  const importancesQ = usePatientImportance();

  // Search typeaheads — debounced 300ms before the lookup fires. (Language moved to
  // PatientPreferencesFields with its own field — PROD-1988.)

  const [religionQuery, setReligionQuery] = useState('');
  const debouncedReligion = useDebounce(religionQuery, 300);
  const religionsQ = useReligions(debouncedReligion);
  const [nationalityQuery, setNationalityQuery] = useState('');
  const debouncedNationality = useDebounce(nationalityQuery, 300);
  const nationalitiesQ = useNationalities(debouncedNationality);

  return (
    <div className="grid gap-6">
      {/* First name */}
      <div>
        <FieldLabel required requiredTone={requiredTone(firstName)}>First name</FieldLabel>
        <TextField
          {...register('firstName')}
          placeholder="Enter first name"
          maxLength={50}
          className="mt-1" size="compact"
          aria-invalid={!!errors.firstName}
          status={missing.has('firstName') ? 'error' : undefined}
        />
        {errors.firstName ? (
          <p className="mt-1 text-xs text-status-error">{errors.firstName.message}</p>
        ) : null}
      </div>

      {/* Middle name */}
      <div>
        <FieldLabel>Middle name</FieldLabel>
        <TextField
          {...register('middleName', { setValueAs: (v) => (v === '' ? null : v) })}
          placeholder="Enter middle name (Optional)"
          maxLength={50}
          className="mt-1" size="compact"
        />
      </div>

      {/* Last name */}
      <div>
        <FieldLabel required requiredTone={requiredTone(lastName)}>Last name</FieldLabel>
        <TextField
          {...register('lastName')}
          placeholder="Enter last name"
          maxLength={50}
          className="mt-1" size="compact"
          aria-invalid={!!errors.lastName}
          status={missing.has('lastName') ? 'error' : undefined}
        />
        {errors.lastName ? (
          <p className="mt-1 text-xs text-status-error">{errors.lastName.message}</p>
        ) : null}
      </div>

      {/* Date of birth */}
      <div data-missing-field={missing.has('birthDate') || undefined}>
        <FieldLabel required requiredTone={requiredTone(birthDate)}>Date of birth</FieldLabel>
        <Controller
          control={control}
          name="birthDate"
          render={({ field }) => (
            <DatePickerField
              value={birthToDateInput(field.value)}
              onChange={(v) => field.onChange(dateInputToWire(v))}
              min={dobMin}
              max={dobMax}
              invalid={!!errors.birthDate}
              className="mt-1"
            />
          )}
        />
        {errors.birthDate ? (
          <p className="mt-1 text-xs text-status-error">{errors.birthDate.message}</p>
        ) : null}
      </div>

      {/* Gender (pills) */}
      <div data-missing-field={missing.has('gender') || undefined}>
        <FieldLabel required requiredTone={requiredTone(gender)}>Gender</FieldLabel>
        <div className="mt-2">
          {gendersQ.isPending ? (
            <BrandSpinner size={20} label="Loading…" />
          ) : !gendersQ.data || gendersQ.data.length === 0 ? (
            <span className="text-sm text-text-muted">No genders available</span>
          ) : (
            <PillPicker
              options={gendersQ.data.map((g) => ({ value: g.id, label: g.name }))}
              value={gender || null}
              aria-invalid={!!errors.gender}
              onChange={(v) => {
                setValue('gender', v ?? '', {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                const g = gendersQ.data?.find((x) => x.id === v);
                setValue('genderName', g ? { id: g.id, name: g.name } : null, {
                  shouldDirty: true,
                });
              }}
            />
          )}
        </div>
        {errors.gender ? (
          <p className="mt-1 text-xs text-status-error">{errors.gender.message}</p>
        ) : null}
      </div>

      {/* Consent to share data with HIE (pills, allowUnselect=false) */}
      <div>
        <FieldLabel>Consent to share data with HIE</FieldLabel>
        <div className="mt-2">
          <PillPicker
            options={CONSENT_OPTIONS.map((o) => ({ value: o.code, label: o.display }))}
            value={consent?.code ?? null}
            allowUnselect={false}
            onChange={(v) => {
              const opt = CONSENT_OPTIONS.find((o) => o.code === v);
              setValue(
                'consent',
                opt
                  ? { code: opt.code, display: opt.display, definition: opt.definition }
                  : null,
                { shouldDirty: true },
              );
            }}
          />
        </div>
      </div>

      {/* Importance (pills) */}
      <div>
        <FieldLabel>Importance</FieldLabel>
        <div className="mt-2">
          {importancesQ.isPending ? (
            <BrandSpinner size={20} label="Loading…" />
          ) : (
            <PillPicker
              options={(importancesQ.data ?? []).map((i) => ({
                value: i.code,
                label: i.display,
              }))}
              value={importance?.code ?? null}
              onChange={(v) => {
                const item = importancesQ.data?.find((i) => i.code === v);
                setValue(
                  'importance',
                  item
                    ? {
                        code: item.code,
                        display: item.display,
                        definition: item.definition ?? null,
                      }
                    : null,
                  { shouldDirty: true },
                );
              }}
            />
          )}
        </div>
      </div>

      {/* Marital status · Preferred language · Interpreter required — extracted to
          PatientPreferencesFields (PROD-1988) so the chart's Demog tab renders the SAME
          three controls; Flutter's chart Demog tab shows exactly these three. */}
      <PatientPreferencesFields />

      {/* Religion (search) */}
      <div>
        <FieldLabel>Religion</FieldLabel>
        <div className="mt-1">
          <MasterTypeahead
            value={
              religion?.code || religion?.display
                ? {
                    id: religion.code ?? null,
                    name: religion.display ?? null,
                    code: religion.code ?? null,
                    display: religion.display ?? null,
                    system: religion.system ?? null,
                    description: religion.description ?? null,
                    shortName: religion.shortName ?? null,
                  }
                : null
            }
            onSelect={(item) =>
              setValue(
                'religion',
                item
                  ? {
                      code: item.code,
                      display: item.display,
                      system: item.system,
                      description: item.description,
                      shortName: item.shortName,
                    }
                  : null,
                { shouldDirty: true },
              )
            }
            placeholder="Enter Religion"
            query={religionQuery}
            onQueryChange={setReligionQuery}
            results={religionsQ.data ?? []}
            isPending={religionsQ.isFetching}
          />
        </div>
      </div>

      {/* Nationality (search, required) */}
      <div>
        <FieldLabel required requiredTone={requiredTone(nationality?.code || nationality?.display)}>Nationality</FieldLabel>
        <div className="mt-1">
          <MasterTypeahead
            value={
              nationality?.code || nationality?.display
                ? {
                    id: nationality.code ?? null,
                    // Selected trigger shows the `code` (demonym, "Indian"),
                    // matching Flutter `patient.nationality.code`
                    // (personal_details_form.dart:848-850) and the dropdown
                    // label above — not the `display` country name.
                    name: nationality.code ?? null,
                    code: nationality.code ?? null,
                    display: nationality.display ?? null,
                    system: nationality.system ?? null,
                  }
                : null
            }
            onSelect={(item) =>
              setValue(
                'nationality',
                item
                  ? {
                      code: item.code,
                      display: item.display,
                      system: item.system,
                    }
                  : null,
                { shouldDirty: true },
              )
            }
            invalid={missing.has('nationality')}
            placeholder="Enter Nationality"
            query={nationalityQuery}
            onQueryChange={setNationalityQuery}
            results={nationalitiesQ.data ?? []}
            isPending={nationalitiesQ.isFetching}
          />
        </div>
      </div>
    </div>
  );
}
